import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Banknote, Save, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button, SearchSelect } from '../../shared';
import { useToastContext } from '../../../contexts/ToastContext';
import {
  adminSettlementSettingsService,
  clearSettlementCurrencyCache,
} from '../../../services/settlementCurrencyService';

const COMMON_CURRENCIES = ['USD', 'CAD', 'EUR', 'GBP', 'AUD', 'NZD'];

const SettlementSettings = () => {
  const queryClient = useQueryClient();
  const toast = useToastContext();
  const [form, setForm] = useState({
    settlement_currency: 'CAD',
    display_currency: 'USD',
    fx_rate: '1',
    stripe_fee_percent: '2.9',
    stripe_fee_fixed: '0.30',
    stripe_fee_bearer: 'platform',
    allowed_settlement_currencies: ['USD', 'CAD'],
    display_locale: 'en-US',
    is_change_locked: false,
    change_lock_reason: '',
    confirm_currency: '',
    notes: '',
    align_plan_currencies: false,
  });
  const [allowlistInput, setAllowlistInput] = useState('USD,CAD');

  const { data: response, isLoading } = useQuery({
    queryKey: ['adminSettlementSettings'],
    queryFn: () => adminSettlementSettingsService.get(),
  });

  const settings = response?.data;

  useEffect(() => {
    if (!settings) return;
    const allowed = settings.allowed_currencies || ['USD', 'CAD'];
    setForm((prev) => ({
      ...prev,
      settlement_currency: settings.currency || 'USD',
      display_currency: settings.display_currency || 'USD',
      fx_rate: String(settings.fx_rate ?? 1),
      stripe_fee_percent: String(settings.stripe_fee_percent ?? 2.9),
      stripe_fee_fixed: String(settings.stripe_fee_fixed ?? 0.3),
      stripe_fee_bearer: settings.stripe_fee_bearer || 'platform',
      allowed_settlement_currencies: allowed,
      display_locale: settings.display_locale || 'en-US',
      is_change_locked: !!settings.is_change_locked,
      change_lock_reason: settings.change_lock_reason || '',
      confirm_currency: '',
      notes: '',
      align_plan_currencies: false,
    }));
    setAllowlistInput(allowed.join(','));
  }, [settings]);

  const currencyChanged =
    settings?.currency &&
    form.settlement_currency &&
    form.settlement_currency.toUpperCase() !== String(settings.currency).toUpperCase();

  const currencyOptions = useMemo(() => {
    const fromAllowlist = (form.allowed_settlement_currencies || []).map((c) => ({
      value: c,
      label: c,
    }));
    const extras = COMMON_CURRENCIES.filter(
      (c) => !form.allowed_settlement_currencies?.includes(c)
    ).map((c) => ({ value: c, label: `${c} (add to allowlist first)` }));
    return [...fromAllowlist, ...extras];
  }, [form.allowed_settlement_currencies]);

  const saveMutation = useMutation({
    mutationFn: (payload) => adminSettlementSettingsService.save(payload),
    onSuccess: (res) => {
      clearSettlementCurrencyCache();
      queryClient.invalidateQueries(['adminSettlementSettings']);
      toast.success(null, res?.message || 'Settlement settings saved');
      setForm((f) => ({ ...f, confirm_currency: '', notes: '', align_plan_currencies: false }));
    },
    onError: (err) => {
      toast.error(null, err.response?.data?.message || 'Failed to save settlement settings');
    },
  });

  const handleAllowlistBlur = () => {
    const parsed = allowlistInput
      .split(/[\s,]+/)
      .map((c) => c.trim().toUpperCase())
      .filter((c) => /^[A-Z]{3}$/.test(c));
    const unique = [...new Set(parsed)];
    if (unique.length === 0) {
      toast.error(null, 'Allowlist must include at least one ISO currency code');
      return;
    }
    setForm((f) => ({
      ...f,
      allowed_settlement_currencies: unique,
      settlement_currency: unique.includes(f.settlement_currency)
        ? f.settlement_currency
        : unique[0],
    }));
    setAllowlistInput(unique.join(','));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      settlement_currency: form.settlement_currency.toUpperCase(),
      display_currency: form.display_currency.toUpperCase(),
      fx_rate: parseFloat(form.fx_rate),
      stripe_fee_percent: parseFloat(form.stripe_fee_percent),
      stripe_fee_fixed: parseFloat(form.stripe_fee_fixed),
      stripe_fee_bearer: form.stripe_fee_bearer,
      allowed_settlement_currencies: form.allowed_settlement_currencies,
      display_locale: form.display_locale || null,
      is_change_locked: form.is_change_locked,
      change_lock_reason: form.change_lock_reason || null,
      align_plan_currencies: form.align_plan_currencies,
    };
    if (currencyChanged) {
      payload.confirm_currency = (form.confirm_currency || '').toUpperCase();
      payload.notes = form.notes || null;
    }
    saveMutation.mutate(payload);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 mx-auto" />
          <p className="mt-3 text-sm text-gray-600">Loading settlement settings…</p>
        </div>
      </div>
    );
  }

  const health = settings?.health;

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
      <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-slate-50 to-white p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-slate-900 text-white p-2.5">
            <Banknote className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Platform settlement currency</h3>
            <p className="mt-1 text-sm text-gray-600">
              Clients see catalog prices in the <strong>display currency</strong> (e.g. USD). Stripe
              charges in the <strong>settlement currency</strong> (e.g. CAD) so your Stripe account
              does not take an FX conversion fee. Admin commission is calculated on the order;
              Stripe processing fees come out of that commission — vendors stay whole.
            </p>
            <p className="mt-2 text-sm font-medium text-gray-900">
              Charge:{' '}
              <span className="font-mono text-base tracking-wide">
                {settings?.currency || form.settlement_currency}
              </span>
              {' · '}
              Display:{' '}
              <span className="font-mono text-base tracking-wide">
                {settings?.display_currency || form.display_currency}
              </span>
              {' · '}
              FX:{' '}
              <span className="font-mono text-base tracking-wide">
                1 {settings?.display_currency || form.display_currency} ={' '}
                {settings?.fx_rate ?? form.fx_rate}{' '}
                {settings?.currency || form.settlement_currency}
              </span>
            </p>
          </div>
        </div>
      </div>

      {health && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Plans matching</p>
            <p className="mt-1 text-xl font-semibold text-gray-900">
              {health.plans_matching}/{health.plans_total}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Plan mismatches</p>
            <p
              className={`mt-1 text-xl font-semibold ${
                health.plans_mismatch > 0 ? 'text-amber-600' : 'text-emerald-600'
              }`}
            >
              {health.plans_mismatch}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Status</p>
            <p className="mt-1 text-sm font-medium text-gray-900 flex items-center gap-1.5">
              {health.plans_mismatch === 0 ? (
                <>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Aligned
                </>
              ) : (
                <>
                  <AlertTriangle className="w-4 h-4 text-amber-600" /> Align plans
                </>
              )}
            </p>
          </div>
        </div>
      )}

      <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Allowed currencies</label>
          <input
            type="text"
            value={allowlistInput}
            onChange={(e) => setAllowlistInput(e.target.value.toUpperCase())}
            onBlur={handleAllowlistBlur}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black font-mono text-sm"
            placeholder="USD,CAD"
          />
          <p className="mt-1 text-xs text-gray-500">
            Comma-separated ISO codes. Only these can be selected as the active settlement currency.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Active settlement currency (Stripe charge)</label>
          <SearchSelect
            value={form.settlement_currency}
            onChange={(value) =>
              setForm((f) => ({ ...f, settlement_currency: String(value || '').toUpperCase() }))
            }
            options={currencyOptions.filter((o) =>
              form.allowed_settlement_currencies.includes(o.value)
            )}
            placeholder="Select currency"
          />
          <p className="mt-1 text-xs text-gray-500">
            Must match your Stripe payout / bank currency (e.g. CAD) to avoid conversion fees.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display currency (frontend)</label>
            <input
              type="text"
              maxLength={3}
              value={form.display_currency}
              onChange={(e) =>
                setForm((f) => ({ ...f, display_currency: e.target.value.toUpperCase() }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
              placeholder="USD"
            />
            <p className="mt-1 text-xs text-gray-500">Catalog / booking sidebar prices (e.g. USD).</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              FX rate (settlement per 1 display)
            </label>
            <input
              type="number"
              step="0.00000001"
              min="0.00000001"
              value={form.fx_rate}
              onChange={(e) => setForm((f) => ({ ...f, fx_rate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
              placeholder="1.38464"
            />
            <p className="mt-1 text-xs text-gray-500">
              Example: 1 USD = 1.38464 CAD → enter 1.38464. Locked onto each payment.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 rounded-lg border border-gray-100 bg-gray-50 p-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Stripe fee %</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.stripe_fee_percent}
              onChange={(e) => setForm((f) => ({ ...f, stripe_fee_percent: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Stripe fixed fee ({form.settlement_currency})
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.stripe_fee_fixed}
              onChange={(e) => setForm((f) => ({ ...f, stripe_fee_fixed: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Who pays Stripe fees</label>
            <select
              value={form.stripe_fee_bearer}
              onChange={(e) => setForm((f) => ({ ...f, stripe_fee_bearer: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
            >
              <option value="platform">Platform (from commission)</option>
              <option value="passthrough" disabled>
                Pass to client (soon)
              </option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Display locale</label>
          <input
            type="text"
            value={form.display_locale}
            onChange={(e) => setForm((f) => ({ ...f, display_locale: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black text-sm"
            placeholder="en-US"
          />
        </div>

        <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-3">
          <input
            type="checkbox"
            id="is_change_locked"
            checked={form.is_change_locked}
            onChange={(e) => setForm((f) => ({ ...f, is_change_locked: e.target.checked }))}
            className="mt-1 w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
          />
          <div>
            <label htmlFor="is_change_locked" className="text-sm font-medium text-gray-900">
              Lock currency changes
            </label>
            <p className="text-xs text-gray-500">
              Prevent switching the active currency until unlocked (use during cutovers).
            </p>
            {form.is_change_locked && (
              <input
                type="text"
                value={form.change_lock_reason}
                onChange={(e) => setForm((f) => ({ ...f, change_lock_reason: e.target.value }))}
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="Lock reason (optional)"
              />
            )}
          </div>
        </div>

        <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-3">
          <input
            type="checkbox"
            id="align_plan_currencies"
            checked={form.align_plan_currencies}
            onChange={(e) => setForm((f) => ({ ...f, align_plan_currencies: e.target.checked }))}
            className="mt-1 w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
          />
          <div>
            <label htmlFor="align_plan_currencies" className="text-sm font-medium text-gray-900">
              Align all subscription plan currencies to the active settlement currency
            </label>
            <p className="text-xs text-gray-500">
              Updates plan rows in the database. You still need matching Stripe Price IDs in that
              currency.
            </p>
          </div>
        </div>

        {currencyChanged && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold">Currency cutover confirmation required</p>
                <p className="mt-1">
                  Changing from <strong>{settings.currency}</strong> to{' '}
                  <strong>{form.settlement_currency}</strong> affects all new booking and
                  subscription charges. Disburse leftover balances in the old currency first when
                  possible.
                </p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-amber-900 mb-1">
                Type {form.settlement_currency} to confirm
              </label>
              <input
                type="text"
                value={form.confirm_currency}
                onChange={(e) =>
                  setForm((f) => ({ ...f, confirm_currency: e.target.value.toUpperCase() }))
                }
                className="w-full px-3 py-2 border border-amber-300 rounded-md font-mono text-sm bg-white"
                placeholder={form.settlement_currency}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-amber-900 mb-1">Notes (optional)</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-amber-300 rounded-md text-sm bg-white"
                placeholder="Cutover notes for audit log"
              />
            </div>
          </div>
        )}
      </div>

      {Array.isArray(settings?.history) && settings.history.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Change history</h4>
          <ul className="space-y-2">
            {settings.history.map((row) => (
              <li
                key={row.id}
                className="flex flex-wrap items-center justify-between gap-2 text-sm border-b border-gray-100 pb-2 last:border-0"
              >
                <span className="font-mono">
                  {row.from_currency} → {row.to_currency}
                </span>
                <span className="text-gray-500">
                  {row.changer?.name || 'Admin'} ·{' '}
                  {row.effective_at ? new Date(row.effective_at).toLocaleString() : '—'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-end">
        <Button type="submit" loading={saveMutation.isPending} className="inline-flex items-center gap-2">
          <Save className="w-4 h-4" />
          Save settlement settings
        </Button>
      </div>
    </form>
  );
};

export default SettlementSettings;
